/**
 * Tutorial Step Definitions
 *
 * Defines all tutorial steps with their content, triggers, and actions.
 * This data-driven approach makes it easy to add/modify steps without code changes.
 */

export const TUTORIAL_STEPS = {
  // ============================================================================
  // Phase 1: Initial Onboarding (New Account)
  // ============================================================================

  'welcome': {
    id: 'welcome',
    phase: 1,
    title: 'Welcome to Pokesume!',
    content: "I'm Professor Oak. I'll guide you through the first steps of your Pokemon training journey!",
    tips: [
      'Train Pokemon to compete in battles',
      'Defeat Gym Leaders and challenge the Elite Four',
      'Build your collection through Gacha pulls'
    ],
    screen: 'menu',
    showOverlay: true,
    nextStep: 'starter-select',
    autoAdvance: false
  },

  'starter-select': {
    id: 'starter-select',
    phase: 1,
    title: 'Choose Your Starter',
    content: 'Every trainer begins with a starter Pokemon. Each has unique strengths - choose one that suits you!',
    tips: [
      'Charmander goes all in on big attacks',
      'Squirtle is a well-balanced battler',
      'Bulbasaur debuffs its opponents',
      'Pikachu shocks with quick attacks',
      'Gastly is an elusive target'
    ],
    screen: 'menu',
    showOverlay: true,
    highlightSelector: '.starter-grid',
    nextStep: 'career-goal',
    autoAdvance: false,
    waitForAction: 'starterSelected'
  },

  'career-goal': {
    id: 'career-goal',
    phase: 1,
    title: 'Your Goal',
    content: 'Train your Pokemon over 60 turns. Defeat 4 Gym Leaders at turns 12, 24, 36, and 48, then challenge the Elite Four!',
    tips: [
      'Each turn you can train one stat',
      'Losing to a Gym Leader ends your career',
      'Trained Pokemon join your Hall of Fame'
    ],
    screen: 'menu',
    showOverlay: true,
    nextStep: 'career-start',
    autoAdvance: false
  },

  // ============================================================================
  // Phase 2: First Career (Guided)
  // ============================================================================

  'career-start': {
    id: 'career-start',
    phase: 2,
    title: 'Start Your Career',
    content: "Let's begin your first career! Select your starter Pokemon to start training.",
    tips: [
      'Your starter is ready to train',
      "We'll guide you through the first career"
    ],
    screen: 'pokemonSelect',
    showOverlay: true,
    highlightSelector: '.pokemon-card',
    nextStep: 'inspiration-skip',
    autoAdvance: false,
    forceStarterSelection: true
  },

  'inspiration-skip': {
    id: 'inspiration-skip',
    phase: 2,
    title: 'Inspirations',
    content: "Inspirations come from Pokemon in your Hall of Fame. You don't have any trained Pokemon yet, so we'll skip this for now.",
    tips: [
      'Train Pokemon to add them to your Hall of Fame',
      'Hall of Fame Pokemon can inspire future trainees'
    ],
    screen: 'inspirationSelect',
    showOverlay: true,
    nextStep: 'support-auto',
    autoAdvance: true,
    autoSkipScreen: true
  },

  'support-auto': {
    id: 'support-auto',
    phase: 2,
    title: 'Support Trainers',
    content: "Support Trainers boost your training! I'll select some good ones for your first run.",
    tips: [
      'Support Trainers give bonuses when training matching types',
      'Build friendship with Support Trainers for bigger bonuses',
      'Support Trainers also hint at moves your Pokemon can learn'
    ],
    screen: 'supportSelect',
    showOverlay: true,
    nextStep: 'training-intro',
    autoAdvance: true,
    autoSelectSupports: true
  },

  'training-intro': {
    id: 'training-intro',
    phase: 2,
    title: 'Training Your Pokemon',
    content: 'Tap on a bar to train that stat. Training uses energy. The lower your energy, the higher chance the training fails. Rest to recover energy!',
    tips: [
      'HP, Attack, Defense, Instinct, Speed',
      'Training can succeed or fail based on energy',
      'Failed training still uses energy',
      'Rest to recover energy when low'
    ],
    screen: 'career',
    showOverlay: true,
    highlightSelector: '.training-bars',
    nextStep: 'battle-intro',
    autoAdvance: false,
    waitForAction: 'firstTraining',
    showOnce: true
  },

  'battle-intro': {
    id: 'battle-intro',
    phase: 2,
    title: 'Wild Battles',
    content: "You can battle wild Pokemon to gain bonus stats and skill points! Click the Battle button to see available opponents.",
    tips: [
      'Wild battles cost 25 energy',
      'Winning gives +15 to a random stat and +20 skill points',
      'At 0 energy, you cannot battle wild Pokemon'
    ],
    screen: 'career',
    showOverlay: true,
    nextStep: 'battle-select',
    autoAdvance: false,
    showOnce: true
  },

  'battle-select': {
    id: 'battle-select',
    phase: 2,
    title: 'Choose Your Opponent',
    content: "Pick a wild Pokemon to battle! Type advantages matter - choose wisely based on your Pokemon's type and moves.",
    tips: [
      'Each opponent has different types and moves',
      'Consider type matchups before engaging',
      'Click Battle to start the fight!'
    ],
    screen: 'career',
    showOverlay: true,
    nextStep: 'mood-intro',
    autoAdvance: false,
    showOnce: true
  },

  'mood-intro': {
    id: 'mood-intro',
    phase: 2,
    title: 'Managing Mood',
    content: "Your Pokemon's mood affects training success! High mood means better results. Click Relax to spend a turn improving your Pokemon's mood.",
    tips: [
      'High mood increases training results',
      'Low mood reduces training results',
      'Relaxing uses a turn but boosts mood',
      'Watch the mood indicator in the header'
    ],
    screen: 'career',
    showOverlay: true,
    highlightSelector: '.tutorial-relax-button',
    nextStep: 'move-intro',
    autoAdvance: false,
    showOnce: true,
    waitForAction: 'relaxPerformed'
  },

  'move-intro': {
    id: 'move-intro',
    phase: 2,
    title: 'Skill Points',
    content: 'You earned skill points! Click the blue book icon to see moves you can learn.',
    tips: [
      'Skill points are earned from training and battles',
      'Spend them to learn new moves'
    ],
    screen: 'career',
    showOverlay: true,
    highlightSelector: '.tutorial-sp-book',
    nextStep: 'move-learn',
    autoAdvance: false,
    showOnce: true,
    triggerOnSkillPoint: true
  },

  'move-learn': {
    id: 'move-learn',
    phase: 2,
    title: 'Learning Moves',
    content: "This is the Learn screen. Moves cost skill points to learn.",
    tips: [
      'Moves have different power, stamina cost, and effects',
      'Support Trainers hint at good moves',
      'You can equip many moves, but using them will be gated by stamina'
    ],
    screen: 'career',
    showOverlay: true,
    highlightSelector: '.tutorial-stab-move',
    nextStep: 'rest-intro',
    autoAdvance: false,
    showOnce: true
  },

  'rest-intro': {
    id: 'rest-intro',
    phase: 2,
    title: 'Rest to Recover',
    content: 'Your energy is getting low! Training and battles use energy. At 0 energy, training has a 99% fail rate. Rest to recover!',
    tips: [
      'Resting restores 30-70 energy',
      'Low energy means training will likely fail',
      'Plan your rests between training sessions'
    ],
    screen: 'career',
    showOverlay: true,
    highlightSelector: '.tutorial-rest-button',
    nextStep: 'gym-intro',
    autoAdvance: false,
    showOnce: true,
    triggerOnLowEnergy: true
  },

  'gym-intro': {
    id: 'gym-intro',
    phase: 2,
    title: 'Gym Battle!',
    content: "It's time to face your first Gym Leader! Win to continue your career. Lose and it's game over!",
    tips: [
      'Gym Leaders have scaled stats based on turn',
      'Type advantages matter in battle',
      'Your moves and strategy determine success'
    ],
    screen: 'career',
    showOverlay: true,
    nextStep: 'gacha-intro',
    autoAdvance: false,
    showOnce: true,
    triggerAtTurn: 12
  },

  'pokeclock-first-defeat': {
    id: 'pokeclock-first-defeat',
    phase: 2,
    title: 'Pokeclock Granted!',
    content: "You've been granted a Pokeclock! Pokeclocks let you retry gym battles and Elite Four battles after a defeat without ending your career.",
    tips: [
      'Defeat gym leaders to earn 1 Pokeclock',
      'Defeat Elite Four members to earn 1 Pokeclock',
      'Use Pokeclocks to retry battles immediately',
      "Turn doesn't advance when you use a Pokeclock"
    ],
    screen: 'career',
    showOverlay: true,
    nextStep: 'gacha-intro',
    autoAdvance: false,
    showOnce: true
  },

  'pokeclock-usage': {
    id: 'pokeclock-usage',
    phase: 2,
    title: 'Use Your Pokeclock!',
    content: "You have a Pokeclock available! Since you lost this battle, you can use your Pokeclock to retry without ending your career.",
    tips: [
      'Using a Pokeclock lets you retry immediately',
      'You earn Pokeclocks by defeating gym leaders',
      "Turn doesn't advance when you use a Pokeclock"
    ],
    screen: 'career',
    showOverlay: true,
    nextStep: null,
    autoAdvance: false,
    showOnce: true
  },

  // ============================================================================
  // Phase 3: Post-First-Career (After Loss/Win)
  // ============================================================================

  'gacha-intro': {
    id: 'gacha-intro',
    phase: 3,
    title: 'Strengthen Your Collection',
    content: "Great effort on your first career! Now let's expand your collection with a free 10-pull on each gacha!",
    tips: [
      'Free 10-pull on Pokemon Gacha',
      'Free 10-pull on Support Trainer Gacha',
      'Duplicates increase Pokemon power (Limit Break)'
    ],
    screen: 'menu',
    showOverlay: true,
    nextStep: 'pokemon-gacha',
    autoAdvance: false
  },

  'pokemon-gacha': {
    id: 'pokemon-gacha',
    phase: 3,
    title: 'Pokemon Gacha',
    content: "Use your free 10-pull to get new Pokemon! Tap the FREE 10-Roll button.",
    tips: [
      'Common, Uncommon, Rare, and Legendary rarities',
      'Duplicates give Limit Break bonuses',
      'More Pokemon = more team options'
    ],
    screen: 'gacha',
    showOverlay: true,
    highlightSelector: '.gacha-10-pull',
    nextStep: 'support-gacha',
    autoAdvance: false,
    waitForAction: 'gachaPull'
  },

  'support-gacha': {
    id: 'support-gacha',
    phase: 3,
    title: 'Support Trainer Gacha',
    content: "Now use your free 10-pull to get Support Trainers! Tap the FREE 10-Roll button.",
    tips: [
      'Support Trainers have different specialties',
      'Match Support Trainer types with your Pokemon',
      'Higher rarity = better bonuses'
    ],
    screen: 'supportGacha',
    showOverlay: true,
    highlightSelector: '.gacha-10-pull',
    nextStep: 'new-career-prompt',
    autoAdvance: false,
    waitForAction: 'gachaPull'
  },

  'new-career-prompt': {
    id: 'new-career-prompt',
    phase: 3,
    title: 'Start Your Next Career!',
    content: "Now that you have new Pokemon and Support Trainers, let's start another career! Click New Career to begin.",
    tips: [
      'Choose any Pokemon from your collection',
      'Select Support Trainers that match your strategy',
      'Each career is a new adventure!'
    ],
    screen: 'menu',
    showOverlay: true,
    highlightSelector: '.tutorial-new-career',
    nextStep: 'inspiration-intro',
    autoAdvance: false,
    waitForAction: 'newCareerClicked'
  },

  'inspiration-intro': {
    id: 'inspiration-intro',
    phase: 3,
    title: 'Inspirations',
    content: "Now that you have trained Pokemon in your Hall of Fame, you can select them as inspirations! They'll boost your stats and aptitudes.",
    tips: [
      'Inspirations activate at turns 11, 23, 35, 47, and 59',
      'Stars determine how much they boost your Pokemon',
      'Select up to 2 inspirations per career',
      'Pokemon of the same species cannot inspire each other'
    ],
    screen: 'inspirationSelect',
    showOverlay: true,
    nextStep: 'support-select-intro',
    autoAdvance: false,
    showOnce: true
  },

  'support-select-intro': {
    id: 'support-select-intro',
    phase: 3,
    title: 'Choose Your Support Trainers',
    content: "Now you can select your own Support Trainers! Build a squad that matches the strategy you want to employ!",
    tips: [
      'Think about stats and move hints when selecting supports',
      'Build friendship by training together',
      'Experiment with different Support Trainer combinations'
    ],
    screen: 'supportSelect',
    showOverlay: true,
    highlightSelector: '.support-grid',
    nextStep: 'tutorial-complete',
    autoAdvance: false,
    waitForAction: 'supportsSelected'
  },

  'tutorial-complete': {
    id: 'tutorial-complete',
    phase: 3,
    title: 'Tutorial Complete!',
    content: "You're ready to train on your own! Check the Game Guide anytime for more tips. Good luck, trainer!",
    tips: [
      'Game Guide has detailed mechanics info',
      'Try different Pokemon and Support Trainer combos',
      'Aim for the Hall of Fame!',
      'Mewtwo icon unlocked!'
    ],
    screen: 'career',
    showOverlay: true,
    nextStep: null,
    autoAdvance: false,
    completeTutorial: true,
    grantMewtwoIcon: true
  }
};

/**
 * Get step data by ID
 */
export const getStepData = (stepId) => {
  return TUTORIAL_STEPS[stepId] || null;
};

/**
 * Get all steps for a specific phase
 */
export const getStepsByPhase = (phase) => {
  return Object.values(TUTORIAL_STEPS).filter(step => step.phase === phase);
};

/**
 * Check if a step should show overlay
 */
export const shouldShowOverlay = (stepId) => {
  const step = TUTORIAL_STEPS[stepId];
  return step?.showOverlay ?? false;
};

/**
 * Get step content for display
 */
export const getStepContent = (stepId) => {
  const step = TUTORIAL_STEPS[stepId];
  if (!step) return null;

  return {
    title: step.title,
    content: step.content,
    tips: step.tips || [],
    highlightSelector: step.highlightSelector
  };
};
